import { keccak_256 as keccak } from '@noble/hashes/sha3.js';
import { secp256k1 } from '@noble/curves/secp256k1.js';
import Exchange from '../abstract/prediction/hyperliquid.js';
import { Precise } from '../base/Precise.js';
import { ecdsa } from '../base/functions/crypto.js';
import type {
    Int, int, Str, Num, Dict,
    Market, PredictionOrderBook, OHLCV,
    Balances, fetchEventsParams,
    Strings,
    PredictionEvent, PredictionTicker, PredictionTickers, PredictionOrder, PredictionTrade, PredictionPosition,
} from '../base/types.js';
import { ArgumentsRequired, ExchangeError, OrderNotFound, InvalidOrder, InsufficientFunds, RateLimitExceeded } from '../base/errors.js';

// ---------------------------------------------------------------------------

/**
 * @class hyperliquid
 * @augments Exchange
 */
export default class hyperliquid extends Exchange {
    describe (): any {
        return this.deepExtend (super.describe (), {
            'id': 'hyperliquid',
            'name': 'Hyperliquid',
            'countries': [],
            'rateLimit': 50,
            'certified': false,
            'pro': false,
            'dex': true,
            'has': {
                'CORS': undefined,
                'spot': false,
                'margin': false,
                'swap': false,
                'future': false,
                'option': false,
                'cancelOrder': true,
                'cancelOrders': true,
                'createOrder': true,
                'fetchBalance': true,
                'fetchCurrencies': false,
                'fetchEvents': true,
                'fetchMarkets': true,
                'fetchMyTrades': true,
                'fetchOHLCV': true,
                'fetchOpenOrders': true,
                'fetchOrder': true,
                'fetchOrderBook': true,
                'fetchOrders': true,
                'fetchPositions': true,
                'fetchTicker': true,
                'fetchTickers': true,
                'fetchTrades': true,
                'prediction': true,
            },
            'timeframes': {
                '1m': '1m',
                '3m': '3m',
                '5m': '5m',
                '15m': '15m',
                '30m': '30m',
                '1h': '1h',
                '2h': '2h',
                '4h': '4h',
                '8h': '8h',
                '12h': '12h',
                '1d': '1d',
                '3d': '3d',
                '1w': '1w',
                '1M': '1M',
            },
            'urls': {
                'logo': 'https://github.com/user-attachments/assets/550769b3-d270-461e-9e02-8e8b8c0210b8',
                'api': {
                    'public': 'https://api.hyperliquid.xyz',
                    'private': 'https://api.hyperliquid.xyz',
                },
                'test': {
                    'public': 'https://api.hyperliquid-testnet.xyz',
                    'private': 'https://api.hyperliquid-testnet.xyz',
                },
                'www': 'https://hyperliquid.xyz',
                'doc': 'https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api',
                'fees': 'https://hyperliquid.gitbook.io/hyperliquid-docs/trading/fees',
                'referral': 'https://app.hyperliquid.xyz/',
            },
            'api': {
                'public': {
                    'post': {
                        'info': {
                            'cost': 20,
                            'byType': {
                                'l2Book': 2,
                                'allMids': 2,
                                'spotClearinghouseState': 2,
                                'candleSnapshot': 4,
                                'orderStatus': 2,
                            },
                        },
                    },
                },
                'private': {
                    'post': {
                        'exchange': 1,
                    },
                },
            },
            'requiredCredentials': {
                'apiKey': false,
                'secret': false,
                'walletAddress': true,
                'privateKey': true,
            },
            'fees': {
                'trading': {
                    'tierBased': false,
                    'percentage': true,
                    'maker': 0.0002,
                    'taker': 0.0005,
                },
            },
            'options': {
                'defaultType': 'prediction',
                // the whole outcome universe is one cheap outcomeMeta request, so bulk-warming on
                // a cache miss stays the right trade-off here (the base default is false)
                'loadAllOutcomes': true,
                'sandboxMode': false,  // outcome markets currently deployed on testnet
                'outcomeQuoteCurrency': 'USDH',
                'defaultSlippage': 0.05,
                'zeroAddress': '0x0000000000000000000000000000000000000000',
                'builderFee': false,
            },
            'exceptions': {
                'exact': {
                    'Order was never placed, already canceled, or filled.': OrderNotFound,
                    'Insufficient spot balance': InsufficientFunds,
                    'Too many cumulative requests sent': RateLimitExceeded,
                    'Order has zero size.': InvalidOrder,
                    'Order has invalid size': InvalidOrder,
                    'Order price cannot be more than 80% away from the reference price': InvalidOrder,
                    'No liquidity available for market order.': InvalidOrder,
                },
                'broad': {
                    'Insufficient': InsufficientFunds,
                },
            },
        });
    }

    setSandboxMode (enabled: boolean) {
        super.setSandboxMode (enabled);
        this.options['sandboxMode'] = enabled;
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#outcomeEncoding
     * @description computes the encoding for an outcome side: encoding = 10 * outcomeId + side (side 0 = YES, side 1 = NO)
     * @param {int} outcomeId integer outcome id
     * @param {int} side outcome side, 0 = YES, 1 = NO
     * @returns {int} the outcome side encoding
     */
    outcomeEncoding (outcomeId: number, side: number): number {
        return this.sum (10 * outcomeId, side);
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#outcomeAssetId
     * @description returns the asset id used for orders: 100_000_000 + encoding, e.g. 100000010
     * @param {int} encoding outcome side encoding
     * @returns {int} the asset id
     */
    outcomeAssetId (encoding: number): number {
        return this.sum (100000000, encoding);
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#outcomeCoin
     * @description returns the coin name used in API calls: #<encoding>, e.g. #10 for outcome 1 side 0
     * @param {int} encoding outcome side encoding
     * @returns {string} the coin name
     */
    outcomeCoin (encoding: number): string {
        return '#' + encoding.toString ();
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#outcomeToken
     * @description returns the token name: +<encoding>, e.g. +10
     * @param {int} encoding outcome side encoding
     * @returns {string} the token name
     */
    outcomeToken (encoding: number): string {
        return '+' + encoding.toString ();
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#parseOutcomeDescription
     * @description parses a description string of the form class:priceBinary|underlying:BTC|expiry:20260503-0600|targetPrice:78213|period:1d into a dict
     * @param {string} description the raw outcome description string
     * @returns {object} a dict of the parsed key/value pairs
     */
    parseOutcomeDescription (description: string): Dict {
        if (!description) {
            return {};
        }
        const parts = description.split ('|');
        const result = {};
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            const colonIndex = part.indexOf (':');
            if (colonIndex > -1) {
                const key = part.slice (0, colonIndex);
                const value = part.slice (colonIndex + 1);
                result[key] = value;
            }
        }
        return result;
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#buildOutcomeSymbol
     * @description builds a human-readable outcome from a parsed description and side, e.g. BTC_ABOVE_78213_20260503:YES for side 0 and BTC_ABOVE_78213_20260503:NO for side 1
     * @param {object} desc parsed outcome description
     * @param {int} side outcome side, 0 = YES, 1 = NO
     * @param {int} outcomeId integer outcome id
     * @returns {string} the outcome
     */
    buildOutcomeSymbol (desc: Dict, side: number, outcomeId: number): string {
        const underlying = this.safeString (desc, 'underlying', 'OUTCOME' + outcomeId.toString ());
        const targetPrice = this.safeString (desc, 'targetPrice');
        const expiry = this.safeString (desc, 'expiry', '');
        // Parse expiry: "20260503-0600" → "20260503"
        const expiryDate = expiry ? expiry.split ('-')[0] : '';
        const label = (side === 0) ? 'YES' : 'NO';
        let base = underlying.toUpperCase ();
        if (targetPrice) {
            base = base + '_ABOVE_' + targetPrice;
        }
        if (expiryDate) {
            base = base + '_' + expiryDate;
        }
        return base + ':' + label;
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#buildOutcomeParentSymbol
     * @description builds a market id (parent outcome without YES/NO) from a parsed description, e.g. BTC_ABOVE_78213_20260503 for priceBinary outcomes or OUTCOME_9345 for non-priceBinary outcomes using the name field
     * @param {object} desc parsed outcome description
     * @param {int} outcomeId integer outcome id
     * @param {string} [name] outcome name
     * @param {object} [question] linked question object from outcomeMeta
     * @returns {string} the parent market outcome
     */
    buildOutcomeParentSymbol (desc: Dict, outcomeId: number, name = '', question: Dict = {}): string {
        const underlying = this.safeString (desc, 'underlying');
        if (underlying) {
            const targetPrice = this.safeString (desc, 'targetPrice');
            const expiry = this.safeString (desc, 'expiry', '');
            const expiryDate = expiry ? expiry.split ('-')[0] : '';
            let base = underlying.toUpperCase ();
            if (targetPrice) {
                base = base + '_ABOVE_' + targetPrice;
            }
            if (expiryDate) {
                base = base + '_' + expiryDate;
            }
            return base;
        }
        const questionDescription = this.safeString (question, 'description');
        if (questionDescription) {
            const questionDesc = this.parseOutcomeDescription (questionDescription);
            const questionClass = this.safeStringLower (questionDesc, 'class');
            if (questionClass === 'pricebucket') {
                const questionUnderlying = this.safeString (questionDesc, 'underlying');
                const questionExpiry = this.safeString (questionDesc, 'expiry', '');
                const expiryDate = questionExpiry ? questionExpiry.split ('-')[0] : '';
                const thresholdsRaw = this.safeString (questionDesc, 'priceThresholds', '');
                const indexStr = this.safeString (desc, 'index');
                const rawDescription = this.safeStringLower (desc, 'description', '');
                const nameLower = name.toLowerCase ();
                if (questionUnderlying && thresholdsRaw && indexStr !== undefined) {
                    const thresholdParts = thresholdsRaw.split (',');
                    const thresholds = [];
                    for (let i = 0; i < thresholdParts.length; i++) {
                        const trimmed = thresholdParts[i].trim ();
                        if (trimmed.length > 0) {
                            thresholds.push (trimmed);
                        }
                    }
                    const thresholdsLength = thresholds.length;
                    const index = this.parseToInt (indexStr);
                    if (thresholdsLength > 0 && index !== undefined) {
                        let bucketLabel: string;
                        if (index <= 0) {
                            bucketLabel = 'BELOW_' + thresholds[0];
                        } else if (index >= thresholdsLength) {
                            const lastIdx = thresholdsLength - 1;
                            bucketLabel = 'ABOVE_' + thresholds[lastIdx];
                        } else {
                            bucketLabel = 'BETWEEN_' + thresholds[index - 1] + '_' + thresholds[index];
                        }
                        let base = questionUnderlying.toUpperCase () + '_' + bucketLabel;
                        if (expiryDate) {
                            base = base + '_' + expiryDate;
                        }
                        return base;
                    }
                }
                const isFallbackLike = (rawDescription === 'other') || (nameLower.indexOf ('fallback') >= 0) || (nameLower.indexOf ('other') >= 0);
                if (questionUnderlying && isFallbackLike) {
                    let base = questionUnderlying.toUpperCase () + '_OTHER';
                    if (expiryDate) {
                        base = base + '_' + expiryDate;
                    }
                    return base;
                }
            }
        }
        const questionName = this.safeString (question, 'name');
        if (questionName) {
            const questionSlug = this.shortenSlug (questionName);
            if (questionSlug) {
                let outcomeSlug = this.shortenSlug (name);
                const genericOutcomeNames = {
                    'RECURRING': true,
                    'RECURRING_FALLBACK': true,
                    'RECURRING_NAMED_OUTCOME': true,
                };
                if (outcomeSlug in genericOutcomeNames) {
                    if (outcomeSlug.indexOf ('FALLBACK') >= 0) {
                        outcomeSlug = 'OTHER';
                    } else {
                        outcomeSlug = '';
                    }
                }
                if (outcomeSlug) {
                    return questionSlug + '_' + outcomeSlug + '_' + outcomeId.toString ();
                }
                return questionSlug + '_' + outcomeId.toString ();
            }
        }
        // Fallback: use name slugified, or OUTCOME-<id>
        if (name) {
            return this.shortenSlug (name) + '_' + outcomeId.toString ();
        }
        return 'OUTCOME_' + outcomeId.toString ();
    }

    /**
     * @method
     * @name hyperliquid#fetchMarkets
     * @description Retrieves all Hyperliquid outcome markets from outcomeMeta.
     * Each binary outcome becomes one CCXT prediction market with two outcomes: YES and NO.
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/asset-ids#outcomes
     * @param {object} [params] extra parameters
     * @returns {Market[]} array of market structures
     */
    async fetchMarkets (params = {}): Promise<Market[]> {
        //
        // outcomeMeta response:
        //
        // {
        //   "outcomes": [
        //     {
        //       "outcome": 9345,
        //       "name": "Recurring",
        //       "description": "class:priceBinary|underlying:BTC|expiry:20260513-0300|targetPrice:81023|period:1d",
        //       "sideSpecs": [
        //         { "name": "Yes" },
        //         { "name": "No" }
        //       ]
        //     },
        //     ...
        //   ],
        //   "questions": [
        //     {
        //       "question": 182,
        //       "name": "What will Hypurr eat the most of in May 2026?",
        //       "description": "...",
        //       "fallbackOutcome": 7002,
        //       "namedOutcomes": [7003, 7004, 7005],
        //       "settledNamedOutcomes": []
        //     },
        //     ...
        //   ]
        // }
        //
        const response = await this.publicPostInfo (this.extend ({ 'type': 'outcomeMeta' }, params));
        const outcomesList = this.safeList (response, 'outcomes', []);
        const questionsList = this.safeList (response, 'questions', []);
        const outcomesToQuestions = {};
        for (let qi = 0; qi < questionsList.length; qi++) {
            const question = this.safeDict (questionsList, qi, {});
            const fallbackOutcome = this.safeInteger (question, 'fallbackOutcome');
            if (fallbackOutcome !== undefined) {
                const fallbackKey = fallbackOutcome.toString ();
                outcomesToQuestions[fallbackKey] = question;
            }
            const namedOutcomes = this.safeList (question, 'namedOutcomes', []);
            for (let ni = 0; ni < namedOutcomes.length; ni++) {
                const namedOutcomeId = this.safeInteger (namedOutcomes, ni);
                if (namedOutcomeId !== undefined) {
                    const namedKey = namedOutcomeId.toString ();
                    outcomesToQuestions[namedKey] = question;
                }
            }
        }
        const markets = [];
        if (this.outcomes === undefined) {
            this.outcomes = {};
        }
        if (this.outcomes_by_id === undefined) {
            this.outcomes_by_id = {};
        }
        for (let i = 0; i < outcomesList.length; i++) {
            const outcomeInfo = this.safeDict (outcomesList, i, {});
            const outcomeId = this.safeInteger (outcomeInfo, 'outcome', i);
            const linkedQuestion = this.safeDict (outcomesToQuestions, outcomeId.toString (), {});
            const market = this.parseOutcomeMarket (outcomeInfo, outcomeId, linkedQuestion);
            markets.push (market);
            // Build outcomes dictionary from market outcomes
            const marketOutcomes = this.safeList (market as any, 'outcomes', []);
            for (let oi = 0; oi < marketOutcomes.length; oi++) {
                const outcome = this.safeDict (marketOutcomes, oi, {});
                const outcomeSymbol = this.safeString2 (outcome, 'outcome', 'symbol');
                const outcomeId_ = this.safeString2 (outcome, 'outcomeId', 'id');
                if (outcomeSymbol !== undefined) {
                    this.outcomes[outcomeSymbol] = outcome;
                }
                if (outcomeId_ !== undefined) {
                    this.outcomes_by_id[outcomeId_] = outcome;
                }
            }
        }
        return markets;
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#parseOutcomeMarket
     * @description parses a single binary outcome market into a CCXT market structure with outcomes[]
     * @param {object} outcomeInfo raw entry from outcomeMeta outcomes array
     * @param {int} outcomeId integer outcome id
     * @param {object} [question] linked question object from outcomeMeta questions array
     * @returns {object} a [market structure](https://docs.ccxt.com/#/?id=market-structure)
     */
    parseOutcomeMarket (outcomeInfo: Dict, outcomeId: number, question: Dict = {}): Market {
        const description = this.safeString (outcomeInfo, 'description', '');
        const name = this.safeString (outcomeInfo, 'name', '');
        const sideSpecs = this.safeList (outcomeInfo, 'sideSpecs', []);
        const desc = this.parseOutcomeDescription (description);
        const parentSymbol = this.buildOutcomeParentSymbol (desc, outcomeId, name, question);
        const yesEncoding = this.outcomeEncoding (outcomeId, 0);
        const noEncoding = this.outcomeEncoding (outcomeId, 1);
        const yesOutcomeSymbol = parentSymbol + ':YES';
        const noOutcomeSymbol = parentSymbol + ':NO';
        // Parse expiry from description
        const expiry = this.safeString (desc, 'expiry');
        let expiryMs = undefined;
        let expiryDatetime = undefined;
        if (expiry) {
            // e.g. "20260503-0600" → "2026-05-03T06:00:00Z"
            const expParts = expiry.split ('-');
            const expPartsLength = expParts.length;
            if (expPartsLength >= 1 && expParts[0].length === 8) {
                const ymd = expParts[0];
                const hm = (expPartsLength >= 2) ? expParts[1] : '0000';
                const isoStr = ymd.slice (0, 4) + '-' + ymd.slice (4, 6) + '-' + ymd.slice (6, 8) + 'T' + hm.slice (0, 2) + ':' + hm.slice (2, 4) + ':00Z';
                expiryMs = this.parse8601 (isoStr);
                expiryDatetime = isoStr;
            }
        }
        // Side labels from sideSpecs (e.g. "Yes"/"No", but use YES/NO normalised)
        const yesLabel = this.safeStringUpper (this.safeDict (sideSpecs, 0, {}), 'name', 'YES');
        const noLabel = this.safeStringUpper (this.safeDict (sideSpecs, 1, {}), 'name', 'NO');
        const quoteCurrency = this.safeString (this.options, 'outcomeQuoteCurrency', 'USDH');
        const szDecimals = 4;  // outcomes use 4 decimal places
        const active = true;
        const outcomePrecision = {
            'amount': this.parseNumber (this.parsePrecision (szDecimals.toString ())),
            'price': 0.0001,
        };
        const outcomes = [
            {
                'id': this.outcomeCoin (yesEncoding),
                'outcomeId': this.outcomeCoin (yesEncoding),
                'outcome': yesOutcomeSymbol,
                'market': parentSymbol,
                'label': yesLabel,
                'active': active,
                'precision': outcomePrecision,
                'info': {
                    'encoding': yesEncoding,
                    'assetId': this.outcomeAssetId (yesEncoding),
                    'coinName': this.outcomeCoin (yesEncoding),
                    'tokenName': this.outcomeToken (yesEncoding),
                    'side': 0,
                    'outcomeId': outcomeId,
                    'name': name,
                    'description': description,
                    'parsedDescription': desc,
                },
            },
            {
                'id': this.outcomeCoin (noEncoding),
                'outcomeId': this.outcomeCoin (noEncoding),
                'outcome': noOutcomeSymbol,
                'market': parentSymbol,
                'label': noLabel,
                'active': active,
                'precision': outcomePrecision,
                'info': {
                    'encoding': noEncoding,
                    'assetId': this.outcomeAssetId (noEncoding),
                    'coinName': this.outcomeCoin (noEncoding),
                    'tokenName': this.outcomeToken (noEncoding),
                    'side': 1,
                    'outcomeId': outcomeId,
                    'name': name,
                    'description': description,
                    'parsedDescription': desc,
                },
            },
        ];
        const marketRow = this.safeMarketStructure ({
            'id': outcomeId.toString (),
            'market': parentSymbol,
            'base': parentSymbol.split ('/')[0],
            'quote': quoteCurrency,
            'settle': undefined,
            'baseId': outcomeId.toString (),
            'quoteId': quoteCurrency,
            'settleId': undefined,
            'type': 'prediction',
            'marketType': 'binary',
            'executionModel': 'clob',
            'spot': false,
            'margin': undefined,
            'swap': false,
            'future': false,
            'option': false,
            'prediction': true,
            'active': active,
            'contract': false,
            'linear': undefined,
            'inverse': undefined,
            'taker': 0.0005,
            'maker': 0.0002,
            'contractSize': undefined,
            'expiry': expiryMs,
            'expiryDatetime': expiryDatetime,
            'strike': undefined,
            'optionType': undefined,
            'percentage': true,
            'tierBased': false,
            'feeSide': 'get',
            'precision': outcomePrecision,
            'limits': {
                'leverage': { 'min': undefined, 'max': undefined },
                'amount': { 'min': undefined, 'max': undefined },
                'price': { 'min': 0.0001, 'max': 0.9999 },
                'cost': { 'min': this.parseNumber ('1'), 'max': undefined },
            },
            'outcomes': outcomes,
            'info': this.extend (outcomeInfo, {
                'outcomeId': outcomeId,
                'parentSymbol': parentSymbol,
                'description': description,
                'parsedDescription': desc,
            }),
            'created': undefined,
        });
        // omit the deprecated 'symbol' key the safeMarketStructure template injects —
        // prediction market rows carry only the unified 'market' handle
        return this.omit (marketRow, 'symbol') as Market;
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#calculatePricePrecision
     * @description calculates an appropriate price precision tick size given midPx and szDecimals
     * @param {float} midPx the mid price
     * @param {int} szDecimals the number of size decimals
     * @returns {float} the price tick size
     */
    calculatePricePrecision (midPx: number, szDecimals: number): number {
        if (midPx <= 0) {
            return 0.0001;
        }
        const midStr = this.numberToString (midPx);
        const parts = midStr.split ('.');
        const intPart = parts[0];
        const significantDigits = Math.max (5, intPart.length);
        const maxDecimals = 8 - szDecimals;
        const pricePrecisionDecimals = Math.max (1, Math.min (maxDecimals, significantDigits - intPart.length));
        let zeros = '';
        const zeroCount = pricePrecisionDecimals - 1;
        for (let zi = 0; zi < zeroCount; zi++) {
            zeros = zeros + '0';
        }
        return this.parseToNumeric ('0.' + zeros + '1');
    }

    /**
     * @method
     * @name hyperliquid#fetchTicker
     * @description fetches a ticker for a single outcome market using the L2 order book snapshot
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/info-endpoint#l2-book-snapshot
     * @param {string} outcome unified outcome (e.g. 'BTC_ABOVE_78213_20260503:YES')
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [prediction ticker structure](https://docs.ccxt.com/#/?id=prediction-ticker-structure)
     */
    async fetchTicker (outcome: string, params = {}): Promise<PredictionTicker> {
        await this.loadOutcome (outcome);
        const outcomeObj = this.outcome (outcome);
        const info = this.safeDict (outcomeObj, 'info', {});
        const coin = this.safeString (info, 'coinName');
        const request = {
            'type': 'l2Book',
            'coin': coin,
        };
        const response = await this.publicPostInfo (this.extend (request, params));
        //
        //     {
        //         "coin": "#10",
        //         "levels": [
        //             [ { "n": "2", "px": "0.44", "sz": "500" } ],   // bids [0]
        //             [ { "n": "2", "px": "0.46", "sz": "400" } ]    // asks [1]
        //         ],
        //         "time": 1704290104840
        //     }
        //
        // l2Book returns null for coins without an order book; coerce to an empty dict
        const tickerData = this.safeDict ({ 'book': response }, 'book', {});
        return this.parsePredictionTicker (tickerData, outcomeObj);
    }

    /**
     * @method
     * @name hyperliquid#fetchTickers
     * @description fetches all outcome market tickers using allMids then optionally enriches with l2Book
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/info-endpoint#retrieve-all-mids-for-all-actively-traded-coins
     * @param {string[]} [outcomes] filter by outcome ids or outcomes
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a dictionary of [prediction ticker structures](https://docs.ccxt.com/#/?id=prediction-ticker-structure)
     */
    async fetchTickers (outcomes: Strings = undefined, params = {}): Promise<PredictionTickers> {
        const requestedOutcomeSymbols = {};
        if (outcomes !== undefined) {
            // one warm-up for the whole list (a cold cache bulk-loads once via loadAllOutcomes),
            // then identities resolve synchronously
            await this.loadOutcomes (outcomes);
            for (let i = 0; i < outcomes.length; i++) {
                const requested = outcomes[i];
                const requestedOutcomeObj = this.safeOutcome (requested);
                const requestedOutcome = this.safeString (requestedOutcomeObj, 'outcome', requested);
                requestedOutcomeSymbols[requestedOutcome] = true;
            }
        } else {
            // no filter — warm the whole outcome set so identities resolve from the cache
            await this.loadOutcomes ();
        }
        const response = await this.publicPostInfo (this.extend ({ 'type': 'allMids' }, params));
        //
        // { "mids": { "#10": "0.45", "#11": "0.55", ... } }
        //
        const mids = this.safeDict (response, 'mids', response);
        const tickers: PredictionTickers = {};
        const outcomesMap = (this.outcomes !== undefined) ? this.outcomes : {};
        const outcomeHandles = Object.keys (outcomesMap);
        for (let i = 0; i < outcomeHandles.length; i++) {
            const outcomeHandle = outcomeHandles[i];
            if (outcomes !== undefined && !(outcomeHandle in requestedOutcomeSymbols)) {
                continue;
            }
            const outcomeObj = this.safeDict (outcomesMap, outcomeHandle, {});
            const info = this.safeDict (outcomeObj, 'info', {});
            const coin = this.safeString (info, 'coinName');
            const mid = this.safeNumber (mids, coin);
            if (mid === undefined) {
                continue;
            }
            // Build minimal ticker from mid price
            const ticker = this.parsePredictionTicker ({ 'levels': [ [], [] ], 'mid': mid, 'time': this.milliseconds () }, outcomeObj as any);
            tickers[outcomeHandle] = ticker;
        }
        return tickers;
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#parsePredictionTicker
     * @description parses a raw l2Book response (or a synthetic mid dict) into a unified ticker object
     * @param {object} raw l2Book response or { mid, time } object
     * @param {object} [market] the market the ticker belongs to
     * @returns {object} a [prediction ticker structure](https://docs.ccxt.com/#/?id=prediction-ticker-structure)
     */
    parsePredictionTicker (raw: Dict, market: Market = undefined): PredictionTicker {
        //
        //     {
        //         "coin": "#10",
        //         "levels": [
        //             [ { "n": "2", "px": "0.44", "sz": "500" } ],   // bids [0]
        //             [ { "n": "2", "px": "0.46", "sz": "400" } ]    // asks [1]
        //         ],
        //         "time": 1704290104840
        //     }
        //
        const now = this.milliseconds ();
        const timestamp = this.safeInteger (raw, 'time', now);
        // the 2nd arg carries the outcome object (callers pass the resolved outcome)
        const mkt = this.safeOutcome (undefined, market);
        const outcome = this.safeString (mkt, 'outcome');
        const levels = this.safeList (raw, 'levels', []);
        const rawBids = this.safeList (levels, 0, []);
        const rawAsks = this.safeList (levels, 1, []);
        const topBid = this.safeDict (rawBids, 0);
        const topAsk = this.safeDict (rawAsks, 0);
        const bid = (topBid !== undefined) ? this.safeNumber (topBid, 'px') : undefined;
        const ask = (topAsk !== undefined) ? this.safeNumber (topAsk, 'px') : undefined;
        const bidVolume = (topBid !== undefined) ? this.safeNumber (topBid, 'sz') : undefined;
        const askVolume = (topAsk !== undefined) ? this.safeNumber (topAsk, 'sz') : undefined;
        // Use synthetic mid if no l2Book
        let mid = this.safeNumber (raw, 'mid');
        if (mid === undefined && bid !== undefined && ask !== undefined) {
            mid = this.sum (bid, ask) / 2;
        }
        // day volume lives on the parent market's ctx; resolve it from the outcome's parent market
        const parentSymbol = this.safeString (mkt, 'market');
        const parentMarket = (parentSymbol !== undefined) ? this.safeMarket (parentSymbol) : undefined;
        const ctx = (parentMarket !== undefined) ? this.safeDict (this.safeDict (parentMarket as any, 'info', {}), 'ctx', {}) : {};
        const dayVolume = this.safeNumber (ctx, 'dayNtlVlm');
        return this.safePredictionTicker ({
            'outcome': outcome,
            'outcomeId': this.safeString2 (mkt, 'outcomeId', 'id'),
            'label': this.safeString (mkt, 'label'),
            'market': this.safeString (mkt, 'market'),
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'high': undefined,
            'low': undefined,
            'bid': bid,
            'bidVolume': bidVolume,
            'ask': ask,
            'askVolume': askVolume,
            'vwap': undefined,
            'open': undefined,
            'close': mid,
            'last': mid,
            'previousClose': undefined,
            'change': undefined,
            'percentage': undefined,
            'average': mid,
            'baseVolume': undefined,
            'quoteVolume': dayVolume,
            'info': raw,
        }, market);
    }

    /**
     * @method
     * @name hyperliquid#fetchOrderBook
     * @description fetches the L2 order book for an outcome market
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/info-endpoint#l2-book-snapshot
     * @param {string} outcome unified outcome
     * @param {int} [limit] max depth levels (not used by hyperliquid but accepted)
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [prediction order book structure](https://docs.ccxt.com/#/?id=prediction-order-book-structure)
     */
    async fetchOrderBook (outcome: string, limit: Int = undefined, params = {}): Promise<PredictionOrderBook> {
        await this.loadOutcome (outcome);
        const outcomeObj = this.outcome (outcome);
        const info = this.safeDict (outcomeObj, 'info', {});
        const request = {
            'type': 'l2Book',
            'coin': this.safeString (info, 'coinName'),
        };
        const response = await this.publicPostInfo (this.extend (request, params));
        //
        //     {
        //         "coin": "#10",
        //         "levels": [
        //             [ { "n": "5", "px": "0.44", "sz": "500" }, ... ],   // bids [0]
        //             [ { "n": "5", "px": "0.46", "sz": "400" }, ... ]    // asks [1]
        //         ],
        //         "time": 1704290104840
        //     }
        //
        const timestamp = this.safeInteger (response, 'time');
        const levels = this.safeList (response, 'levels', []);
        const rawBids = this.safeList (levels, 0, []);
        const rawAsks = this.safeList (levels, 1, []);
        const bids = [];
        const asks = [];
        for (let i = 0; i < rawBids.length; i++) {
            const entry = rawBids[i];
            bids.push ([ this.safeNumber (entry, 'px'), this.safeNumber (entry, 'sz') ]);
        }
        for (let i = 0; i < rawAsks.length; i++) {
            const entry = rawAsks[i];
            asks.push ([ this.safeNumber (entry, 'px'), this.safeNumber (entry, 'sz') ]);
        }
        const orderbook = this.parseOrderBook ({ 'bids': bids, 'asks': asks }, this.safeString (outcomeObj, 'outcome', outcome), timestamp);
        return this.safePredictionOrderBook (orderbook, outcomeObj);
    }

    /**
     * @method
     * @name hyperliquid#fetchOHLCV
     * @description fetches candlestick OHLCV data for an outcome market
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/info-endpoint#candle-snapshot
     * @param {string} outcome unified outcome
     * @param {string} timeframe '1m', '5m', '15m', '1h', '4h', '1d', etc.
     * @param {int} [since] timestamp in ms of earliest candle
     * @param {int} [limit] max number of candles
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.until] end timestamp in ms
     * @returns {int[][]} a list of candles ordered as timestamp, open, high, low, close, volume
     */
    async fetchOHLCV (outcome: string, timeframe = '1m', since: Int = undefined, limit: Int = undefined, params = {}): Promise<OHLCV[]> {
        await this.loadOutcome (outcome);
        const outcomeObj = this.outcome (outcome);
        // markets are keyed by the parent market outcome, not the outcome handle ("MARKET:LABEL")
        const market = this.market (this.safeString (outcomeObj, 'market'));
        const info = this.safeDict (outcomeObj, 'info', {});
        const until = this.safeInteger (params, 'until', this.milliseconds ());
        let startTime = since;
        if (since === undefined) {
            const tf = this.parseTimeframe (timeframe);
            const candleCount = (limit !== undefined) ? limit : 100;
            const startOffset = tf * candleCount * -1000;
            startTime = this.sum (until, startOffset);
            if (startTime < 0) {
                startTime = 0;
            }
        }
        const request = {
            'type': 'candleSnapshot',
            'req': {
                'coin': this.safeString (info, 'coinName'),
                'interval': this.safeString (this.timeframes, timeframe, timeframe),
                'startTime': startTime,
                'endTime': until,
            },
        };
        params = this.omit (params, 'until');
        const response = await this.publicPostInfo (this.extend (request, params));
        //
        //     [
        //         {
        //             "T": 1704287699999,   // close time
        //             "c": "0.45",
        //             "h": "0.47",
        //             "i": "1m",
        //             "l": "0.43",
        //             "n": 46,              // number of trades
        //             "o": "0.44",
        //             "s": "#10",
        //             "t": 1704286800000,   // open time
        //             "v": "1234.5"
        //         }
        //     ]
        //
        return this.parseOHLCVs (response, market, timeframe, since, limit);
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#parseOHLCV
     * @description parses a single hyperliquid candle object into a CCXT OHLCV tuple
     * @param {object} ohlcv the raw candle object
     * @param {object} [market] the market the candle belongs to
     * @returns {int[]} a candle ordered as timestamp, open, high, low, close, volume
     */
    parseOHLCV (ohlcv, market: Market = undefined): OHLCV {
        //
        //     {
        //         "T": 1704287699999,   // close time
        //         "c": "0.45",
        //         "h": "0.47",
        //         "i": "1m",
        //         "l": "0.43",
        //         "n": 46,              // number of trades
        //         "o": "0.44",
        //         "s": "#10",
        //         "t": 1704286800000,   // open time
        //         "v": "1234.5"
        //     }
        //
        return [
            this.safeInteger (ohlcv, 't'),
            this.safeNumber (ohlcv, 'o'),
            this.safeNumber (ohlcv, 'h'),
            this.safeNumber (ohlcv, 'l'),
            this.safeNumber (ohlcv, 'c'),
            this.safeNumber (ohlcv, 'v'),
        ];
    }

    /**
     * @method
     * @name hyperliquid#fetchBalance
     * @description Fetches spot balance (outcomes use spot-like balance).
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/info-endpoint/spot#retrieve-a-users-token-balances
     * @param {object} [params] extra parameters
     * @param {string} [params.user] wallet address (defaults to this.walletAddress)
     * @returns {Balances} balance structure
     */
    async fetchBalance (params = {}): Promise<Balances> {
        let userAddress: Str;
        [ userAddress, params ] = this.handlePublicAddress ('fetchBalance', params);
        const request = {
            'type': 'spotClearinghouseState',
            'user': userAddress,
        };
        const response = await this.publicPostInfo (this.extend (request, params));
        //
        //     {
        //         "balances": [
        //             { "coin": "USDC",  "hold": "0.0", "total": "100.0" },
        //             { "coin": "+10",   "hold": "0.0", "total": "50.0" }, // outcome token
        //             { "coin": "+11",   "hold": "0.0", "total": "25.0" }
        //         ]
        //     }
        //
        const result = {
            'info': response,
        };
        const balances = this.safeList (response, 'balances', []);
        for (let i = 0; i < balances.length; i++) {
            const balance = balances[i];
            const coin = this.safeString (balance, 'coin');
            const total = this.safeString (balance, 'total');
            const used = this.safeString (balance, 'hold');
            const account = this.account ();
            account['total'] = total;
            account['used'] = used;
            result[coin] = account;
        }
        return this.safeBalance (result);
    }

    /**
     * @method
     * @name hyperliquid#fetchPositions
     * @description fetches the user's outcome positions; outcome positions are spot token balances under the "+<encoding>" coin form (size and entry notional), the value/entry/mark price/pnl are computed from the current mid prices
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/info-endpoint/spot#retrieve-a-users-token-balances
     * @param {string[]} [outcomes] filter by outcome ids or outcomes
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.user] wallet address
     * @returns {object[]} a list of [prediction position structures](https://docs.ccxt.com/#/?id=prediction-position-structure)
     */
    async fetchPositions (outcomes: Strings = undefined, params = {}): Promise<PredictionPosition[]> {
        const requestedOutcomeSymbols = {};
        if (outcomes !== undefined) {
            // one warm-up for the whole list (a cold cache bulk-loads once via loadAllOutcomes),
            // then identities resolve synchronously
            await this.loadOutcomes (outcomes);
            for (let i = 0; i < outcomes.length; i++) {
                const requested = outcomes[i];
                const requestedOutcomeObj = this.safeOutcome (requested);
                const requestedOutcome = this.safeString (requestedOutcomeObj, 'outcome', requested);
                requestedOutcomeSymbols[requestedOutcome] = true;
            }
        } else {
            // no filter — warm the whole outcome set so identities resolve from the cache
            await this.loadOutcomes ();
        }
        let userAddress: Str;
        [ userAddress, params ] = this.handlePublicAddress ('fetchPositions', params);
        const request = {
            'type': 'spotClearinghouseState',
            'user': userAddress,
        };
        // outcome positions are spot token balances under the "+<encoding>" coin form; they carry
        // the size (total) and entry notional (entryNtl). hyperliquid does not return the position
        // value / entry price / pnl, so they are computed from the current mid prices
        const promises = [
            this.publicPostInfo (this.extend (request, params)),
            this.publicPostInfo ({ 'type': 'allMids' }),
        ];
        const results = await Promise.all (promises);
        const response = results[0];
        const midsResponse = results[1];
        const balances = this.safeList (response, 'balances', []);
        const mids = this.safeDict (midsResponse, 'mids', midsResponse);
        const positions = [];
        for (let i = 0; i < balances.length; i++) {
            const balance = this.safeDict (balances, i, {});
            const coin = this.safeString (balance, 'coin', '');
            // outcome tokens use the "+<encoding>" balance form; skip regular spot tokens (USDC, ...)
            if (coin.indexOf ('+') !== 0) {
                continue;
            }
            const totalStr = this.safeString (balance, 'total');
            if ((totalStr === undefined) || Precise.stringEq (totalStr, '0')) {
                continue;
            }
            // the trade/orderbook form ("#<encoding>") resolves the outcome and the mid price
            const tradeCoin = '#' + coin.slice (1);
            const outcomeObj = this.safeOutcome (tradeCoin);
            if (outcomes !== undefined) {
                const outcomeHandle = this.safeString (outcomeObj, 'outcome');
                if (outcomeHandle === undefined || !(outcomeHandle in requestedOutcomeSymbols)) {
                    continue;
                }
            }
            const enriched = this.extend (balance, { 'markPx': this.safeString (mids, tradeCoin) });
            positions.push (this.parsePredictionPosition (enriched, outcomeObj));
        }
        return positions;
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#parsePredictionPosition
     * @description parses a spot balance entry for an outcome token into a unified position object
     * @param {object} position the raw balance entry
     * @param {object} [market] the outcome object the position belongs to
     * @returns {object} a [prediction position structure](https://docs.ccxt.com/#/?id=prediction-position-structure)
     */
    parsePredictionPosition (position: Dict, market: Market = undefined): PredictionPosition {
        // `position` is a spotClearinghouseState balance entry ({ coin, total, hold, entryNtl })
        // enriched with the current mid price (markPx); hyperliquid does not return the position
        // value / entry price / pnl for outcome tokens, so they are computed here
        const outcomeObj = this.safeOutcome (undefined, market);
        const totalStr = this.safeString (position, 'total');
        const total = this.parseNumber (totalStr);
        const entryNtlStr = this.safeString (position, 'entryNtl');
        let entryPrice = undefined;
        if ((entryNtlStr !== undefined) && (totalStr !== undefined) && !Precise.stringEq (totalStr, '0')) {
            entryPrice = this.parseNumber (Precise.stringDiv (entryNtlStr, totalStr));
        }
        const markPxStr = this.safeString (position, 'markPx');
        let notional = undefined;        // current position value = size * mark price
        let unrealizedPnl = undefined;   // value - entry notional
        if ((markPxStr !== undefined) && (totalStr !== undefined)) {
            const notionalStr = Precise.stringMul (totalStr, markPxStr);
            notional = this.parseNumber (notionalStr);
            if (entryNtlStr !== undefined) {
                unrealizedPnl = this.parseNumber (Precise.stringSub (notionalStr, entryNtlStr));
            }
        }
        return this.safePredictionPosition ({
            'id': undefined,
            'outcome': this.safeString (outcomeObj, 'outcome'),
            'outcomeId': this.safeString2 (outcomeObj, 'outcomeId', 'id'),
            'market': this.safeString (outcomeObj, 'market'),
            'timestamp': undefined,
            'datetime': undefined,
            'isolated': false,
            'hedged': undefined,
            'side': 'long',
            'contracts': total,
            'contractSize': 1,
            'entryPrice': entryPrice,
            'markPrice': this.parseNumber (markPxStr),
            'notional': notional,
            'leverage': undefined,
            'collateral': this.safeNumber (position, 'hold'),
            'initialMargin': undefined,
            'maintenanceMargin': undefined,
            'initialMarginPercentage': undefined,
            'maintenanceMarginPercentage': undefined,
            'unrealizedPnl': unrealizedPnl,
            'realizedPnl': undefined,
            'liquidationPrice': undefined,
            'marginRatio': undefined,
            'marginMode': 'cross',
            'percentage': undefined,
            'info': position,
        });
    }

    findOutcomeInMarket (market: Market, sideHint: Str = undefined): Dict {
        const outcomesList = this.safeList (market as any, 'outcomes', []);
        const normalizedHint = sideHint ? sideHint.toUpperCase () : undefined;
        if (normalizedHint !== undefined) {
            for (let i = 0; i < outcomesList.length; i++) {
                const oc = this.safeDict (outcomesList, i, {});
                const ocSymbol = this.safeString2 (oc, 'outcome', 'symbol', '');
                const ocLabel = this.safeStringUpper (oc, 'label');
                if (ocLabel === normalizedHint || ocSymbol.endsWith (':' + normalizedHint)) {
                    return oc;
                }
            }
        }
        for (let i = 0; i < outcomesList.length; i++) {
            const oc = this.safeDict (outcomesList, i, {});
            const info = this.safeDict (oc, 'info', {});
            if (this.safeInteger (info, 'side') === 0) {
                return oc;
            }
        }
        return this.safeDict (outcomesList, 0, {});
    }

    parseOutcomeInputSideHint (outcomeInput: string): Str {
        if (!outcomeInput) {
            return undefined;
        }
        const colonIndex = outcomeInput.indexOf (':');
        if (colonIndex > -1 && colonIndex < outcomeInput.length - 1) {
            const side = outcomeInput.slice (colonIndex + 1).toUpperCase ();
            if (side === 'YES' || side === 'NO') {
                return side;
            }
        }
        const lower = outcomeInput.toLowerCase ();
        if (lower.endsWith ('-yes')) {
            return 'YES';
        }
        if (lower.endsWith ('-no')) {
            return 'NO';
        }
        return undefined;
    }

    resolveOutcomeInput (outcomeInput: string): Dict {
        if (outcomeInput === undefined) {
            throw new ArgumentsRequired (this.id + ' resolveOutcomeInput() requires an outcome symbol or id');
        }
        if (this.outcomes === undefined || this.outcomes_by_id === undefined) {
            throw new ExchangeError (this.id + ' outcomes not loaded');
        }
        const sideHint = this.parseOutcomeInputSideHint (outcomeInput);
        const candidates = [ outcomeInput ];
        if (outcomeInput.startsWith ('+')) {
            candidates.push ('#' + outcomeInput.slice (1));
        }
        const digitChars = '0123456789';
        const inputChars = this.stringToCharsArray (outcomeInput);
        const inputCharsLength = inputChars.length;
        let isNumericInput = inputCharsLength > 0;
        for (let di = 0; di < inputChars.length; di++) {
            if (digitChars.indexOf (inputChars[di]) < 0) {
                isNumericInput = false;
                break;
            }
        }
        if (isNumericInput) {
            candidates.push ('#' + outcomeInput); // encoding id without #
            const numeric = this.parseToInt (outcomeInput);
            if (numeric !== undefined) {
                candidates.push (this.outcomeCoin (this.outcomeEncoding (numeric, 0))); // raw outcome id -> YES encoding
                candidates.push (this.outcomeCoin (this.outcomeEncoding (numeric, 1))); // raw outcome id -> NO encoding
            }
        }
        for (let i = 0; i < candidates.length; i++) {
            const key = candidates[i];
            if (key in this.outcomes) {
                return this.safeDict (this.outcomes, key, {});
            }
            if (key in this.outcomes_by_id) {
                return this.safeDict (this.outcomes_by_id, key, {});
            }
        }
        if ((outcomeInput in this.markets) || (outcomeInput in this.markets_by_id)) {
            const market = this.safeMarket (outcomeInput);
            const sideHintOrDefault = (sideHint !== undefined) ? sideHint : 'YES';
            const found = this.findOutcomeInMarket (market, sideHintOrDefault);
            if (Object.keys (found).length > 0) {
                return found;
            }
        }
        throw new ArgumentsRequired (this.id + ' cannot resolve outcome from input: ' + outcomeInput + '. Provide an outcome symbol (e.g. MARKET:YES), outcome id (#<encoding>), or market id with side.');
    }

    /**
     * @method
     * @name hyperliquid#createOrder
     * @description creates a limit or market order for an outcome market
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/exchange-endpoint#place-an-order
     * @param {string} outcome unified outcome
     * @param {string} type 'limit' or 'market'
     * @param {string} side 'buy' or 'sell'
     * @param {float} amount quantity of outcome tokens
     * @param {float} [price] limit price (0–1 range for prediction markets)
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.timeInForce] 'Gtc' | 'Ioc' | 'Alo' (default 'Gtc')
     * @param {boolean} [params.postOnly] if true sets timeInForce to 'Alo'
     * @param {boolean} [params.reduceOnly] if true, marks the order as reduce only so it can only decrease an existing position
     * @param {string} [params.slippage] slippage for market orders (default 5%)
     * @param {string} [params.clientOrderId] hex cloid
     * @param {string} [params.vaultAddress] optional subaccount/vault address to trade on behalf of (master signer must be authorized)
     * @returns {object} a [prediction order structure](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async createOrder (outcome: string, type: string, side: string, amount: number, price: Num = undefined, params = {}): Promise<PredictionOrder> {
        await this.initializeClient ();
        await this.loadOutcome (outcome);
        const outcomeObj = this.outcome (outcome);
        // markets are keyed by the parent market outcome; the outcome handle ("MARKET:LABEL")
        // is not a market id, so resolve the market and price/amount precision via outcomeObj['market']
        const marketSymbol = this.safeString (outcomeObj, 'market');
        const market = this.market (marketSymbol);
        const outcomeInfo = this.safeDict (outcomeObj, 'info', {});
        const nonce = this.milliseconds ();
        const isBuy = (side.toUpperCase () === 'BUY');
        const isMarket = (type.toUpperCase () === 'MARKET');
        const assetId = this.safeInteger (outcomeInfo, 'assetId');
        const clientOrderId = this.safeString2 (params, 'clientOrderId', 'client_id');
        const reduceOnly = this.safeBool (params, 'reduceOnly', false);
        const postOnly = this.safeBool (params, 'postOnly', false);
        const defaultSlippage = this.safeString (this.options, 'defaultSlippage', '0.05');
        const slippage = this.safeString (params, 'slippage', defaultSlippage);
        let defaultTif = isMarket ? 'Ioc' : 'Gtc';
        if (postOnly) {
            defaultTif = 'Alo';
        }
        let tif = this.capitalize (this.safeStringLower (params, 'timeInForce', defaultTif)); // eslint-disable-line
        if (price === undefined) {
            if (isMarket) {
                throw new ArgumentsRequired (this.id + ' createOrder() requires a reference price for market orders on outcome markets in between 0 and 1. The exchange uses this reference price together with the configured slippage to derive the execution price.');
            }
            throw new ArgumentsRequired (this.id + ' createOrder() requires a limit price for outcome markets in between 0 and 1.');
        }
        let px: string;
        if (isMarket) {
            const priceStr = this.numberToString (price);
            px = isBuy ? Precise.stringMul (priceStr, Precise.stringAdd ('1', slippage)) : Precise.stringMul (priceStr, Precise.stringSub ('1', slippage));
            px = this.priceToPrecision (marketSymbol, px);
        } else {
            px = this.priceToPrecision (marketSymbol, price);
        }
        const sz = this.amountToPrecision (marketSymbol, amount);
        const orderType = {
            'limit': { 'tif': tif },
        };
        const orderObj = {
            'a': assetId,
            'b': isBuy,
            'p': px,
            's': sz,
            'r': reduceOnly,
            't': orderType,
        };
        if (clientOrderId !== undefined) {
            orderObj['c'] = clientOrderId;
        }
        let vaultAddress = undefined;
        [ vaultAddress, params ] = this.handleOptionAndParams (params, 'createOrder', 'vaultAddress');
        vaultAddress = this.formatVaultAddress (vaultAddress);
        const orderAction = {
            'type': 'order',
            'orders': [ orderObj ],
            'grouping': 'na',
        };
        const signature = this.signL1Action (orderAction, nonce, vaultAddress);
        const request = {
            'action': orderAction,
            'nonce': nonce,
            'signature': signature,
        };
        if (vaultAddress !== undefined) {
            request['vaultAddress'] = vaultAddress;
        }
        const response = await this.privatePostExchange (request);
        //
        //     {
        //         "status": "ok",
        //         "response": {
        //             "type": "order",
        //             "data": { "statuses": [ { "resting": { "oid": 12345 } } ] }
        //         }
        //     }
        //
        const responseObj = this.safeDict (response, 'response', {});
        const data = this.safeDict (responseObj, 'data', {});
        const statuses = this.safeList (data, 'statuses', []);
        const firstStatus = this.safeDict (statuses, 0, {});
        const resting = this.safeDict (firstStatus, 'resting', {});
        const filled = this.safeDict (firstStatus, 'filled', {});
        const oid = this.safeString (resting, 'oid', this.safeString (filled, 'oid'));
        const restingOid = this.safeString (resting, 'oid');
        let orderStatus = 'closed';
        if (restingOid !== undefined) {
            orderStatus = 'open';
        }
        return this.safePredictionOrder ({
            'id': oid,
            'clientOrderId': clientOrderId,
            'info': response,
            'timestamp': nonce,
            'datetime': this.iso8601 (nonce),
            'status': orderStatus,
            'outcome': this.safeString (outcomeObj, 'outcome', outcome),
            'outcomeId': this.safeString (outcomeObj, 'id'),
            'label': this.safeString (outcomeObj, 'label'),
            'market': this.safeString (outcomeObj, 'market'),
            'type': type,
            'side': side,
            'price': price,
            'amount': amount,
            'filled': this.safeNumber (filled, 'totalSz'),
            'remaining': undefined,
            'cost': undefined,
            'fee': undefined,
            'trades': [],
        }, market);
    }

    /**
     * @method
     * @name hyperliquid#cancelOrder
     * @description cancels a single open order
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/exchange-endpoint#cancel-order-s
     * @param {string} id order id
     * @param {string} [outcome] unified outcome
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.clientOrderId] cancel by client order id
     * @param {string} [params.vaultAddress] optional subaccount/vault address to cancel on behalf of
     * @returns {object} a [prediction order structure](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async cancelOrder (id: string, outcome: Str = undefined, params = {}): Promise<PredictionOrder> {
        const orders = await this.cancelOrders ([ id ], outcome, params);
        return this.safeDict (orders, 0) as PredictionOrder;
    }

    /**
     * @method
     * @name hyperliquid#cancelOrders
     * @description cancels multiple open orders
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/exchange-endpoint#cancel-order-s
     * @param {string[]} ids order ids
     * @param {string} [outcome] unified outcome (required)
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [prediction order structures](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async cancelOrders (ids: string[], outcome: Str = undefined, params = {}): Promise<PredictionOrder[]> {
        this.checkRequiredCredentials ();
        if (outcome === undefined) {
            throw new ArgumentsRequired (this.id + ' cancelOrders() requires an outcome argument');
        }
        await this.initializeClient ();
        await this.loadOutcome (outcome);
        const outcomeObj = this.outcome (outcome);
        const outcomeInfo = this.safeDict (outcomeObj, 'info', {});
        const assetId = this.safeInteger (outcomeInfo, 'assetId');
        const nonce = this.milliseconds ();
        const clientOrderId = this.safeValue2 (params, 'clientOrderId', 'client_id');
        params = this.omit (params, [ 'clientOrderId', 'client_id' ]);
        const cancelReq = [];
        const cancelAction = { 'type': 'cancel', 'cancels': [] };
        if (clientOrderId !== undefined) {
            const cloids = Array.isArray (clientOrderId) ? clientOrderId : [ clientOrderId ];
            cancelAction['type'] = 'cancelByCloid';
            for (let i = 0; i < cloids.length; i++) {
                cancelReq.push ({ 'asset': assetId, 'cloid': cloids[i] });
            }
        } else {
            cancelAction['type'] = 'cancel';
            for (let i = 0; i < ids.length; i++) {
                cancelReq.push ({ 'a': assetId, 'o': this.parseToNumeric (ids[i]) });
            }
        }
        cancelAction['cancels'] = cancelReq;
        let vaultAddress = undefined;
        [ vaultAddress, params ] = this.handleOptionAndParams (params, 'cancelOrders', 'vaultAddress');
        vaultAddress = this.formatVaultAddress (vaultAddress);
        const signature = this.signL1Action (cancelAction, nonce, vaultAddress);
        const request = {
            'action': cancelAction,
            'nonce': nonce,
            'signature': signature,
        };
        if (vaultAddress !== undefined) {
            request['vaultAddress'] = vaultAddress;
        }
        const response = await this.privatePostExchange (request);
        const innerResponse = this.safeDict (response, 'response');
        const data = this.safeDict (innerResponse, 'data');
        const statuses = this.safeList (data, 'statuses', []);
        const outcomeSymbol = this.safeString (outcomeObj, 'outcome', outcome);
        let requestIds = ids;
        if (clientOrderId !== undefined) {
            if (Array.isArray (clientOrderId)) {
                requestIds = clientOrderId;
            } else {
                requestIds = [ clientOrderId ];
            }
        }
        const orders = [];
        for (let i = 0; i < statuses.length; i++) {
            const status = statuses[i];
            const error = this.safeString (status, 'error');
            if (error !== undefined) {
                throw new OrderNotFound (this.id + ' cancelOrders() failed for ' + this.safeString (requestIds, i, this.safeString (requestIds, 0)) + ': ' + error);
            }
            const success = (status === 'success') || (this.safeString (status, 'status') === 'success');
            if (!success) {
                throw new ExchangeError (this.id + ' cancelOrders() received an unexpected status: ' + this.json (status));
            }
            const requestId = this.safeString (requestIds, i, this.safeString (requestIds, 0));
            const order = {
                'id': requestId,
                'clientOrderId': (clientOrderId !== undefined) ? requestId : undefined,
                'info': status,
                'status': 'canceled',
                'outcome': outcomeSymbol,
                'outcomeId': this.safeString (outcomeObj, 'id'),
                'label': this.safeString (outcomeObj, 'label'),
                'market': this.safeString (outcomeObj, 'market'),
                'timestamp': this.milliseconds (),
                'datetime': this.iso8601 (this.milliseconds ()),
            };
            orders.push (this.safePredictionOrder (order) as PredictionOrder);
        }
        return orders;
    }

    /**
     * @method
     * @name hyperliquid#fetchOpenOrders
     * @description fetches currently open orders for the user
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/info-endpoint#retrieve-a-users-open-orders
     * @param {string} [outcome] filter by outcome
     * @param {int} [since] only return orders updated since this timestamp in ms
     * @param {int} [limit] max number of orders to return
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.user] wallet address
     * @param {string} [params.method] 'openOrders' | 'frontendOpenOrders' (default)
     * @returns {object[]} a list of [prediction order structures](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async fetchOpenOrders (outcome: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<PredictionOrder[]> {
        let userAddress: Str;
        [ userAddress, params ] = this.handlePublicAddress ('fetchOpenOrders', params);
        let method: Str;
        [ method, params ] = this.handleOptionAndParams (params, 'fetchOpenOrders', 'method', 'frontendOpenOrders');
        const request = { 'type': method, 'user': userAddress };
        const response = await this.publicPostInfo (this.extend (request, params));
        const ordersWithStatus = [];
        for (let i = 0; i < response.length; i++) {
            const order = response[i];
            ordersWithStatus.push (this.extend (order, { 'ccxtStatus': 'open' }));
        }
        const parsed = this.parsePredictionOrders (ordersWithStatus, undefined, since);
        let outcomeHandle: Str = undefined;
        if (outcome !== undefined) {
            await this.loadOutcome (outcome);
            const outcomeObj = this.outcome (outcome);
            outcomeHandle = this.safeString (outcomeObj, 'outcome');
        }
        return this.filterByOutcomeSinceLimit (parsed, outcomeHandle, since, limit) as PredictionOrder[];
    }

    /**
     * @method
     * @name hyperliquid#fetchOrders
     * @description fetches all historical orders for the user
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/info-endpoint#retrieve-a-users-historical-orders
     * @param {string} [outcome] filter by outcome
     * @param {int} [since] only return orders updated since this timestamp in ms
     * @param {int} [limit] max number of orders to return
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.user] wallet address
     * @returns {object[]} a list of [prediction order structures](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async fetchOrders (outcome: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<PredictionOrder[]> {
        let userAddress: Str;
        [ userAddress, params ] = this.handlePublicAddress ('fetchOrders', params);
        const request = { 'type': 'historicalOrders', 'user': userAddress };
        const response = await this.publicPostInfo (this.extend (request, params));
        // Deduplicate by oid keeping most recent statusTimestamp
        const deduped = {};
        for (let i = 0; i < response.length; i++) {
            const raw = response[i];
            let entry = this.safeDict (raw, 'order');
            if (entry === undefined) {
                entry = raw;
            }
            const oid = this.safeString (entry, 'oid');
            if (oid !== undefined) {
                if (!(oid in deduped)) {
                    deduped[oid] = raw;
                } else {
                    const existingTs = this.safeInteger (deduped[oid], 'statusTimestamp');
                    const currentTs = this.safeInteger (raw, 'statusTimestamp');
                    if (currentTs !== undefined && (existingTs === undefined || currentTs > existingTs)) {
                        deduped[oid] = raw;
                    }
                }
            }
        }
        const dedupedValues = Object.values (deduped);
        const parsed = this.parsePredictionOrders (dedupedValues, undefined, since);
        let outcomeHandle: Str = undefined;
        if (outcome !== undefined) {
            await this.loadOutcome (outcome);
            const outcomeObj = this.outcome (outcome);
            outcomeHandle = this.safeString (outcomeObj, 'outcome');
        }
        return this.filterByOutcomeSinceLimit (parsed, outcomeHandle, since, limit) as PredictionOrder[];
    }

    /**
     * @method
     * @name hyperliquid#fetchOrder
     * @description fetches a single order by id
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/info-endpoint#query-order-status-by-oid-or-cloid
     * @param {string} id order id
     * @param {string} [outcome] outcome
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.user] wallet address
     * @param {string} [params.clientOrderId] fetch by client order id instead
     * @returns {object} a [prediction order structure](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    async fetchOrder (id: string, outcome: Str = undefined, params = {}): Promise<PredictionOrder> {
        let userAddress: Str;
        [ userAddress, params ] = this.handlePublicAddress ('fetchOrder', params);
        const clientOrderId = this.safeString (params, 'clientOrderId');
        const request = { 'type': 'orderStatus', 'user': userAddress };
        if (clientOrderId !== undefined) {
            params = this.omit (params, 'clientOrderId');
            request['oid'] = clientOrderId;
        } else {
            const isCloid = id.length >= 34;
            request['oid'] = isCloid ? id : this.parseToNumeric (id);
        }
        const response = await this.publicPostInfo (this.extend (request, params));
        const orderWrapper = this.safeDict (response, 'order', response);
        const parsed = this.parsePredictionOrder (orderWrapper, undefined);
        if (outcome !== undefined) {
            await this.loadOutcome (outcome);
            const outcomeObj = this.outcome (outcome);
            const expected = this.safeString (outcomeObj, 'outcome');
            if (this.safeString (parsed, 'outcome') !== expected) {
                throw new OrderNotFound (this.id + ' fetchOrder() order ' + id + ' is not in outcome ' + expected);
            }
        }
        return parsed;
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#parsePredictionOrder
     * @description parses a raw hyperliquid order object into a unified order object
     * @param {object} order the raw order object
     * @param {object} [market] the market the order belongs to
     * @returns {object} a [prediction order structure](https://docs.ccxt.com/#/?id=prediction-order-structure)
     */
    parsePredictionOrder (order: Dict, market: Market = undefined): PredictionOrder {
        //
        // from frontendOpenOrders:
        // {
        //   "coin": "#10",
        //   "limitPx": "0.44",
        //   "oid": 12345,
        //   "origSz": "100",
        //   "side": "B",
        //   "sz": "100",
        //   "timestamp": 1704346468838
        // }
        //
        // from historicalOrders / orderStatus:
        // {
        //   "order": { "coin": "#10", "limitPx": "0.44", "oid": "12345", "origSz": "100", "side": "B", "sz": "100", "tif": "Gtc", "timestamp": "1704346468838" },
        //   "status": "open",
        //   "statusTimestamp": 1704346468838
        // }
        //
        let entry = this.safeDict (order, 'order', order); // eslint-disable-line
        const status = this.parseOrderStatus (this.safeString2 (order, 'ccxtStatus', 'status'));
        const coin = this.safeString (entry, 'coin');
        const outcomeObj = this.safeOutcome (coin, market as any);
        const marketSymbol = this.safeString (outcomeObj, 'outcome');
        const resolvedMarket = marketSymbol ? this.safeMarket (marketSymbol, market as any) : market;
        const sideRaw = this.safeString (entry, 'side');
        const side = (sideRaw === 'B') ? 'buy' : 'sell';
        const totalAmount = this.safeString (entry, 'origSz');
        const remaining = this.safeString (entry, 'sz');
        let filled = undefined;
        if ((remaining !== undefined) && (totalAmount !== undefined)) {
            filled = Precise.stringSub (totalAmount, remaining);
        }
        const timestamp = this.safeInteger (entry, 'timestamp');
        const tifRaw = this.safeString (entry, 'tif');
        const tif = this.parseTimeInForce (tifRaw);
        const postOnly = (tif === 'PO');
        return this.safePredictionOrder ({
            'id': this.safeString (entry, 'oid'),
            'clientOrderId': this.safeString (entry, 'cloid'),
            'info': order,
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'lastTradeTimestamp': undefined,
            'status': status,
            'outcome': this.safeString (outcomeObj, 'outcome'),
            'outcomeId': this.safeString (outcomeObj, 'id'),
            'label': this.safeString (outcomeObj, 'label'),
            'market': this.safeString (outcomeObj, 'market'),
            'type': this.parseOrderType (this.safeString (entry, 'orderType', 'limit')),
            'timeInForce': tif,
            'postOnly': postOnly,
            'reduceOnly': this.safeBool (entry, 'reduceOnly', false),
            'side': side,
            'price': this.safeNumber (entry, 'limitPx'),
            'triggerPrice': this.safeBool (entry, 'isTrigger') ? this.safeNumber (entry, 'triggerPx') : undefined,
            'amount': this.parseNumber (totalAmount),
            'cost': undefined,
            'average': this.safeNumber (entry, 'avgPx'),
            'filled': this.parseNumber (filled),
            'remaining': this.parseNumber (remaining),
            'fee': undefined,
            'trades': [],
        }, resolvedMarket);
    }

    parseOrderStatus (status: Str): Str {
        const statuses = {
            'triggered': 'open',
            'filled': 'closed',
            'open': 'open',
            'canceled': 'canceled',
            'rejected': 'rejected',
            'marginCanceled': 'canceled',
        };
        if (status === undefined) {
            return undefined;
        }
        if (status.endsWith ('Rejected')) {
            return 'rejected';
        }
        if (status.endsWith ('Canceled')) {
            return 'canceled';
        }
        return this.safeString (statuses, status, status);
    }

    parseOrderType (status: Str): Str {
        const statuses = {
            'stop limit': 'limit',
            'stop market': 'market',
        };
        const statusLower = status ? status.toLowerCase () : undefined;
        return this.safeString (statuses, statusLower, statusLower);
    }

    parseTimeInForce (timeInForce: Str): Str {
        const statuses = {
            'gtc': 'GTC',
            'ioc': 'IOC',
            'fok': 'FOK',
            'alo': 'PO',
        };
        const tifLower = timeInForce ? timeInForce.toLowerCase () : undefined;
        return this.safeString (statuses, tifLower, timeInForce);
    }

    /**
     * @method
     * @name hyperliquid#fetchTrades
     * @description fetches the most recent public trades for an outcome
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/info-endpoint#retrieve-a-coins-recent-trades
     * @param {string} outcome unified outcome
     * @param {int} [since] only return trades at or after this timestamp in ms
     * @param {int} [limit] the maximum number of trades to return
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [prediction trade structures](https://docs.ccxt.com/#/?id=prediction-trade-structure)
     */
    async fetchTrades (outcome: string, since: Int = undefined, limit: Int = undefined, params = {}): Promise<PredictionTrade[]> {
        await this.loadOutcome (outcome);
        const outcomeObj = this.outcome (outcome);
        const info = this.safeDict (outcomeObj, 'info', {});
        const request = {
            'type': 'recentTrades',
            'coin': this.safeString (info, 'coinName'),
        };
        // recentTrades returns the coin's most recent public trades (newest first)
        const response = await this.publicPostInfo (this.extend (request, params));
        const trades = (response) ? response : [];
        return this.parsePredictionTrades (trades, outcomeObj, since, limit);
    }

    /**
     * @method
     * @name hyperliquid#fetchMyTrades
     * @description fetches the authenticated user's fill history
     * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/info-endpoint#retrieve-a-users-fills
     * @param {string} [outcome] filter by outcome
     * @param {int} [since] start timestamp in ms
     * @param {int} [limit] max number of trades to return
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.user] wallet address
     * @param {int} [params.until] end timestamp in ms
     * @returns {object[]} a list of [prediction trade structures](https://docs.ccxt.com/#/?id=prediction-trade-structure)
     */
    async fetchMyTrades (outcome: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<PredictionTrade[]> {
        let outcomeHandle: Str = undefined;
        if (outcome !== undefined) {
            const outcomeObj = await this.loadOutcome (outcome);
            outcomeHandle = this.safeString (outcomeObj, 'outcome');
        } else {
            // fills identify their outcome only by the raw coin handle (e.g. "#10") — warm the
            // cache (one market load) so parsePredictionTrade can resolve the unified outcome identity
            await this.loadOutcomes ();
        }
        let userAddress: Str;
        [ userAddress, params ] = this.handlePublicAddress ('fetchMyTrades', params);
        const request: Dict = { 'user': userAddress };
        if (since !== undefined) {
            request['type'] = 'userFillsByTime';
            request['startTime'] = since;
        } else {
            request['type'] = 'userFills';
        }
        const until = this.safeInteger (params, 'until');
        params = this.omit (params, 'until');
        if (until !== undefined) {
            request['endTime'] = until;
        }
        const response = await this.publicPostInfo (this.extend (request, params));
        const fills = (response) ? response : [];
        // parse without an outcome fallback — fills span every market the wallet traded, so a
        // requested-outcome fallback would mislabel fills whose market is no longer listed
        const parsedTrades = this.parsePredictionTrades (fills as any[], undefined);
        return this.filterByOutcomeSinceLimit (parsedTrades, outcomeHandle, since, limit);
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#parsePredictionTrade
     * @description parses a single hyperliquid fill into a unified trade object
     * @param {object} trade the raw fill object
     * @param {object} [market] the market the trade belongs to
     * @returns {object} a [prediction trade structure](https://docs.ccxt.com/#/?id=prediction-trade-structure)
     */
    parsePredictionTrade (trade: Dict, market: Market = undefined): PredictionTrade {
        //
        // {
        //   "closedPnl": "0.19343",
        //   "coin": "#10",
        //   "crossed": true,
        //   "dir": "Open Long",
        //   "fee": "0.050062",
        //   "feeToken": "USDC",
        //   "hash": "0x...",
        //   "liquidationMarkPx": null,
        //   "oid": 12345,
        //   "px": "0.44",
        //   "side": "B",
        //   "startPosition": "0",
        //   "sz": "100",
        //   "tid": 128423918764978,
        //   "time": 1704262888911
        // }
        //
        const timestamp = this.safeInteger (trade, 'time');
        const price = this.safeString (trade, 'px');
        const amount = this.safeString (trade, 'sz');
        const coin = this.safeString (trade, 'coin');
        const outcomeObj = this.safeOutcome (coin, market as any);
        const marketSymbol = this.safeString (outcomeObj, 'outcome');
        const resolvedMarket = marketSymbol ? this.safeMarket (marketSymbol, market as any) : market;
        const rawSide = this.safeString (trade, 'side');
        const side = (rawSide === 'B') ? 'buy' : 'sell';
        const fee = this.safeNumber (trade, 'fee');
        const feeCurrency = this.safeString (trade, 'feeToken', 'USDC');
        const outcomeSymbol = this.safeString (outcomeObj, 'outcome');
        let feeObject = undefined;
        if (fee !== undefined) {
            feeObject = { 'cost': fee, 'currency': feeCurrency };
        }
        let cost = undefined;
        if ((price !== undefined) && (amount !== undefined)) {
            cost = this.parseNumber (Precise.stringMul (price, amount));
        }
        return this.safePredictionTrade ({
            'id': this.safeString (trade, 'tid'),
            'info': trade,
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'outcome': outcomeSymbol,
            'outcomeId': this.safeString (outcomeObj, 'id'),
            'label': this.safeString (outcomeObj, 'label'),
            'market': this.safeString (outcomeObj, 'market'),
            'order': this.safeString (trade, 'oid'),
            'type': 'limit',
            'side': side,
            'takerOrMaker': this.safeBool (trade, 'crossed') ? 'taker' : 'maker',
            'price': this.parseNumber (price),
            'amount': this.parseNumber (amount),
            'cost': cost,
            'fee': feeObject,
        }, resolvedMarket);
    }

    /**
     * @method
     * @name hyperliquid#fetchEvents
     * @description Groups outcome markets by their underlying (e.g. BTC_ABOVE_78213) into event structures. Each event contains both the YES and NO markets.
     * @param {object} [params] extra parameters
     * @param {string} [params.query] a single query string to filter by (matches description/outcome)
     * @param {string[]} [params.queries] multiple query strings (alternative to query)
     * @returns {PredictionEvent[]} array of event structures
     */
    async fetchEvents (params: fetchEventsParams = {}): Promise<PredictionEvent[]> {
        this.requireEventQuery (params);
        const queries = this.parseSearchQueries (params);
        // hyperliquid has no dedicated events endpoint - events are grouped from the outcome
        // markets. use the cached load so the handles advertised here always match the
        // outcome cache (hyperliquid re-assigns outcome ids over time; a fresh fetch could
        // disagree with a previously warmed cache within the same session)
        const marketsDict = await this.loadMarkets ();
        const marketValues = this.toArray (marketsDict);
        // Group markets by parentSymbol
        const groupMap = {};
        const lowerQueries = [];
        for (let i = 0; i < queries.length; i++) {
            const queryString = queries[i] as string;
            lowerQueries.push (queryString.toLowerCase ());
        }
        const lowerQueriesLength = lowerQueries.length;
        for (let i = 0; i < marketValues.length; i++) {
            const mkt = marketValues[i] as Market;
            if (!this.safeBool (mkt as any, 'prediction', false)) {
                continue;
            }
            const info = this.safeDict (mkt as any, 'info', {});
            const parentSymbol = this.safeString (info, 'parentSymbol', this.safeString2 (mkt as any, 'market', 'symbol'));
            // Apply query filter
            if (lowerQueriesLength > 0) {
                const description = this.safeString (info, 'description', '').toLowerCase ();
                const parentSymbolOrEmpty = (parentSymbol !== undefined) ? parentSymbol : '';
                const symLower = parentSymbolOrEmpty.toLowerCase ();
                // the parentSymbol joins words with underscores (BTC_ABOVE_...), so match the haystack word-by-word
                // and require every word of a query to appear, letting "BTC above" match BTC_ABOVE
                const haystack = description + ' ' + symLower;
                let matches = false;
                for (let qi = 0; qi < lowerQueries.length; qi++) {
                    const words = lowerQueries[qi].split (' ');
                    const wordsLength = words.length;
                    let allWords = true;
                    for (let wi = 0; wi < wordsLength; wi++) {
                        const word = words[wi];
                        // `< 0` (not `=== -1`) — the php transpiler maps `< 0` to `=== false`
                        if ((word !== '') && (haystack.indexOf (word) < 0)) {
                            allWords = false;
                            break;
                        }
                    }
                    if (allWords) {
                        matches = true;
                        break;
                    }
                }
                if (!matches) {
                    continue;
                }
            }
            if (!(parentSymbol in groupMap)) {
                groupMap[parentSymbol] = [];
            }
            // push through a local and write the slice back — the go transpiler's
            // AppendToArray reassigns only a local copy of a map-stored array, so a
            // direct push on groupMap[parentSymbol] loses the element in go
            const parentMarkets = groupMap[parentSymbol] as any[];
            parentMarkets.push (mkt);
            groupMap[parentSymbol] = parentMarkets;
        }
        const events = [];
        const groupKeys = Object.keys (groupMap);
        for (let gi = 0; gi < groupKeys.length; gi++) {
            const key = groupKeys[gi];
            const groupMarkets = groupMap[key] as any[];
            const event = this.parseEvent ({ 'parentSymbol': key, 'markets': groupMarkets });
            events.push (event);
        }
        // applyEventFetchParams caches via setEvents (keyed by id/slug/handle) before filtering,
        // so getEvent() resolves these events by any of the three keys
        return this.applyEventFetchParams (events, params, queries);
    }

    /**
     * @ignore
     * @method
     * @name hyperliquid#parseEvent
     * @description parses a grouped set of outcome markets into a unified prediction event object
     * @param {object} raw a dict with parentSymbol and markets entries
     * @returns {object} an event structure
     */
    parseEvent (raw: Dict): any {
        const parentSymbol = this.safeString (raw, 'parentSymbol');
        const markets = this.safeList (raw, 'markets', []) as any[];
        // Extract info from first market
        const marketsLength = markets.length;
        const firstMarket = (marketsLength > 0) ? markets[0] : {};
        const firstInfo = this.safeDict (firstMarket as any, 'info', {});
        const desc = this.safeDict (firstInfo, 'parsedDescription', {});
        const underlying = this.safeString (desc, 'underlying');
        const targetPrice = this.safeString (desc, 'targetPrice');
        const expiryRaw = this.safeString (desc, 'expiry');
        let expiryMs = undefined;
        let expiryDatetime = undefined;
        if (expiryRaw) {
            const parts = expiryRaw.split ('-');
            const partsLength = parts.length;
            if (partsLength >= 1 && parts[0].length === 8) {
                const ymd = parts[0];
                const hm = (partsLength >= 2) ? parts[1] : '0000';
                const isoStr = ymd.slice (0, 4) + '-' + ymd.slice (4, 6) + '-' + ymd.slice (6, 8) + 'T' + hm.slice (0, 2) + ':' + hm.slice (2, 4) + ':00Z';
                expiryMs = this.parse8601 (isoStr);
                expiryDatetime = isoStr;
            }
        }
        const firstExpiry = this.safeInteger (firstMarket as any, 'expiry');
        let title = parentSymbol;
        if (underlying !== undefined) {
            let titleSuffix = '';
            if (targetPrice) {
                titleSuffix = titleSuffix + ' ABOVE ' + targetPrice;
            }
            if (expiryRaw) {
                titleSuffix = titleSuffix + ' @ ' + expiryRaw;
            }
            title = underlying + titleSuffix;
        }
        const endValue = (expiryMs !== undefined) ? expiryMs : firstExpiry;
        return this.extend ({
            'id': parentSymbol,
            'slug': parentSymbol,
            'event': parentSymbol,
            'title': title,
            'markets': markets,
            'underlying': underlying,
            'targetPrice': targetPrice,
            'class': this.safeString (desc, 'class'),
            'period': this.safeString (desc, 'period'),
            'url': undefined,
            'image': undefined,
            'created': undefined,
            'createdDatetime': undefined,
            'end': endValue,
            'endDatetime': expiryDatetime,
            'category': 'crypto',
            'lastUpdatedAt': undefined,
            'resolutionSource': 'Hyperliquid mark price',
            'resolved': undefined,
            'info': raw,
        });
    }

    amountToPrecision (outcome: string, amount: any): string {
        const market = this.market (outcome);
        const prec = this.safeNumber (this.safeDict (market as any, 'precision', {}), 'amount', 0.0001);
        // Convert precision to decimal places
        let decimals = 4;
        if (prec > 0) {
            decimals = this.precisionFromString (this.numberToString (prec));
        }
        return this.decimalToPrecision (amount, 1, decimals, 2, this.paddingMode);
    }

    priceToPrecision (outcome: string, price: any): string {
        const market = this.market (outcome);
        const prec = this.safeNumber (this.safeDict (market as any, 'precision', {}), 'price', 0.0001);
        let decimals = 4;
        if (prec > 0) {
            decimals = this.precisionFromString (this.numberToString (prec));
        }
        return this.decimalToPrecision (price, 1, decimals, 2, this.paddingMode);
    }

    hashMessage (message: any): string {
        return '0x' + this.hash (message, keccak, 'hex');
    }

    signHash (hash: string, privateKey: string): Dict {
        const signature = ecdsa (hash.slice (-64), privateKey.slice (-64), secp256k1, undefined);
        // assign to a bare local before padStart — `expr['key'].padStart()` leaks an undefined
        // padStart() call in the PHP transpiler (it only rewrites padStart on a bare identifier)
        const rRaw = signature['r'];
        const sRaw = signature['s'];
        const r = rRaw.padStart (64, '0');
        const s = sRaw.padStart (64, '0');
        return {
            'r': '0x' + r,
            's': '0x' + s,
            'v': this.sum (27, signature['v']), // ecrecover needs v in {27,28}, ecdsa returns the raw {0,1} recovery id
        };
    }

    signMessage (message: any, privateKey: string): Dict {
        return this.signHash (this.hashMessage (message), privateKey.slice (-64));
    }

    constructPhantomAgent (hash: any, isTestnet = true): Dict {
        const source = isTestnet ? 'b' : 'a';
        return {
            'source': source,
            'connectionId': hash,
        };
    }

    actionHash (action: Dict, vaultAddress: Str, nonce: number): any {
        const dataBinary = this.packb (action);
        const dataHex = this.binaryToBase16 (dataBinary);
        let data = dataHex;
        data += '00000' + this.intToBase16 (nonce);
        if (vaultAddress === undefined) {
            data += '00';
        } else {
            data += '01';
            data += vaultAddress;
        }
        return this.hash (this.base16ToBinary (data), keccak, 'binary');
    }

    signL1Action (action: Dict, nonce: number, vaultAddress: Str = undefined): Dict {
        this.checkRequiredCredentials ();
        const hash = this.actionHash (action, vaultAddress, nonce);
        const isTestnet = this.safeBool (this.options, 'sandboxMode', false);
        const phantomAgent = this.constructPhantomAgent (hash, isTestnet);
        const zeroAddress = this.safeString (this.options, 'zeroAddress');
        const domain = {
            'chainId': 1337,
            'name': 'Exchange',
            'verifyingContract': zeroAddress,
            'version': '1',
        };
        const messageTypes = {
            'Agent': [
                { 'name': 'source', 'type': 'string' },
                { 'name': 'connectionId', 'type': 'bytes32' },
            ],
        };
        const msg = this.ethEncodeStructuredData (domain, messageTypes, phantomAgent);
        return this.signMessage (msg, this.privateKey);
    }

    async initializeClient (): Promise<any> {
        // createOrder/createOrders call this before trading; load markets so the order builder can
        // resolve the outcome's market and precision. loading them also keeps this method genuinely
        // async for the PHP and typed transpilers, which mishandle an async body that never suspends
        await this.loadMarkets ();
        const buildFee = this.safeBool (this.options, 'builderFee', false);
        if (!buildFee) {
            return undefined;
        }
        // builder fee approval would go here if needed
        return undefined;
    }

    handlePublicAddress (methodName: string, params: Dict): any {
        let userAux = undefined;
        [ userAux, params ] = this.handleOptionAndParams2 (params, methodName, 'user', 'subAccountAddress');
        let user = userAux;
        [ user, params ] = this.handleOptionAndParams (params, methodName, 'address', userAux);
        if (user !== undefined && user !== '') {
            return [ user, params ];
        }
        if (this.walletAddress !== undefined && this.walletAddress !== '') {
            return [ this.walletAddress, params ];
        }
        throw new ArgumentsRequired (this.id + ' ' + methodName + '() requires a user parameter or walletAddress to be set');
    }

    formatVaultAddress (address: Str = undefined): Str {
        if (address === undefined) {
            return undefined;
        }
        let normalized = address;
        if (normalized.startsWith ('0x') || normalized.startsWith ('0X')) {
            normalized = normalized.slice (2);
        }
        return normalized.toLowerCase ();
    }

    sign (path: any, api: any = 'public', method = 'POST', params = {}, headers: any = undefined, body: any = undefined) {
        const apiGroup = Array.isArray (api) ? api[0] : api;
        const sandboxMode = this.safeBool (this.options, 'sandboxMode', false);
        let baseUrl: string;
        if (sandboxMode) {
            const testUrls = this.safeDict (this.urls, 'test', {});
            baseUrl = this.safeString (testUrls, apiGroup, this.safeString (testUrls, 'public', ''));
        } else {
            const apiUrls = this.safeDict (this.urls, 'api', {});
            baseUrl = this.safeString (apiUrls, apiGroup, this.safeString (apiUrls, 'public', ''));
        }
        const url = baseUrl + '/' + path;
        if (method === 'POST') {
            headers = { 'Content-Type': 'application/json' };
            body = this.json (params);
        }
        return { 'url': url, 'method': method, 'body': body, 'headers': headers };
    }

    handleErrors (code: int, reason: string, url: string, method: string, headers: Dict, body: string, response: any, requestHeaders: any, requestBody: any) {
        if (!response) {
            return undefined;
        }
        const status = this.safeString (response, 'status', '');
        if (status === 'err') {
            const message = this.safeString (response, 'response', body);
            const feedback = this.id + ' ' + body;
            this.throwExactlyMatchedException (this.exceptions['exact'], message, feedback);
            this.throwBroadlyMatchedException (this.exceptions['broad'], message, feedback);
            throw new ExchangeError (feedback);
        }
        // Check for error statuses in order responses
        const responsePayload = this.safeDict (response, 'response', {});
        const data = this.safeDict (responsePayload, 'data', {});
        const statuses = this.safeList (data, 'statuses', []);
        for (let i = 0; i < statuses.length; i++) {
            const message = this.safeString (statuses[i], 'error');
            if (message !== undefined) {
                const feedback = this.id + ' ' + body;
                this.throwExactlyMatchedException (this.exceptions['exact'], message, feedback);
                this.throwBroadlyMatchedException (this.exceptions['broad'], message, feedback);
                throw new ExchangeError (feedback);
            }
        }
        return undefined;
    }

    calculateRateLimiterCost (api, method, path, params, config = {}) {
        if (('byType' in config) && ('type' in params)) {
            const type = params['type'];
            const byType = config['byType'] as Dict;
            if (type in byType) {
                return byType[type];
            }
        }
        return this.safeValue (config, 'cost', 1);
    }
}
